import React, { useEffect, useState } from 'react'
import { FileWithPath, useDropzone } from 'react-dropzone'

import {
  ButtonIcon,
  Icon,
  SpinningLoader,
  Text,
} from '@vallourec-ui/vallourec-ui'

import {
  Container,
  LoadingBarContainer,
  LoadingContainer,
  SpinningContainer,
  TextContainer,
} from './styles'

import Loading from '../Loading'
import { DEFAULT_UPLOAD_FILE_TYPES } from '@/utils/constants'

const UploadBox: React.FC<{
  uploadText: string
  dragAndDropText: string
  rulesText: string
  setData: (file: File | File[], index: number | undefined) => void
  fileTypes?: {
    [key: string]: string[]
  }
  height?: string
  hasPreview?: boolean
  multiple?: boolean
  index?: number | undefined
  setFileRejection?: () => void
}> = ({
  uploadText,
  dragAndDropText,
  rulesText,
  setData,
  fileTypes = DEFAULT_UPLOAD_FILE_TYPES,
  height,
  hasPreview = false,
  multiple = false,
  index,
  setFileRejection,
}) => {
  const [isUploading, setIsUploading] = useState(false)
  const [progress, setProgress] = useState(10)
  const onDrop = async (acceptedFiles: FileWithPath[]) => {
    if (multiple) {
      if (acceptedFiles.length === 1) {
        setIsUploading(true)
        setData(acceptedFiles[0], index)
      }
      if (acceptedFiles.length > 1) {
        setIsUploading(true)
        setData(acceptedFiles, index)
      }
    } else if (acceptedFiles.length === 1) {
      setIsUploading(true)
      setData(acceptedFiles[0], index)
    }
  }

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    accept: fileTypes,
    multiple,
    maxSize: 60000000,
    onDrop,
    disabled: hasPreview ? isUploading : false,
    onDropRejected: setFileRejection,
  })

  useEffect(() => {
    if (isUploading) {
      const interval = setInterval(() => {
        setProgress((prevProgress) =>
          prevProgress >= 100 ? 100 : prevProgress + 10
        )
      }, 1000)

      return () => clearInterval(interval)
    }
  }, [isUploading])

  return (
    <Container
      {...getRootProps({
        isDragActive,
        isDragAccept,
        isDragReject,
      })}
      style={{ minHeight: '126px', height }}
    >
      <input {...getInputProps()} />
      {hasPreview && isUploading ? (
        <LoadingContainer>
          <SpinningContainer>
            <SpinningLoader />
          </SpinningContainer>
          <LoadingBarContainer>
            <Loading progress={progress} />
          </LoadingBarContainer>
        </LoadingContainer>
      ) : (
        <TextContainer>
          <ButtonIcon variant="secondary-grey">
            <Icon name="UploadCloudLine" />
          </ButtonIcon>
          <Text color="brand-color-primary-0" weight="semi-bold" size="text-sm">
            {uploadText}
          </Text>
          <Text color="neutral-color-80" size="text-sm">
            {dragAndDropText}
          </Text>
          <Text color="neutral-color-80" size="text-xs">
            {rulesText}
          </Text>
        </TextContainer>
      )}
    </Container>
  )
}

export default UploadBox
